import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, debounceTime, map, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { PsidService, SurveyService } from '@ps/survey-data-access';

import { AdvancedFiltersLookupParams } from './domain/advanced-filters-lookup-params';
import { CriteriaSearchParams } from './domain/criteria-search-params';
import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from './survey-creation-insights.actions';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

@Injectable()
export class SurveyCreationAdvancedFiltersEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);

  private readonly _surveyService = inject(SurveyService);
  private readonly _psidService = inject(PsidService);

  /**
   * Searches for surveys based on the provided criteria and date range.
   */
  public readonly findSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.findSurveys),
      debounceTime(1000),
      map(
        ({ criteria, dateRange }) =>
          new AdvancedFiltersLookupParams(criteria, dateRange),
      ),
      switchMap((mapper) => {
        if (!mapper.hasCriteriaOrDateRange()) {
          return of(
            SurveyCreationInsightsApiActions.findSurveysSuccess({
              surveys: [],
            }),
          );
        }

        return concat(
          of(SurveyCreationInsightsApiActions.findSurveysLoading()),
          this._surveyService.getSurveySummaries(mapper.build()).pipe(
            map((surveys) =>
              SurveyCreationInsightsApiActions.findSurveysSuccess({
                surveys,
              }),
            ),
            this.psCatchError((error) =>
              of(
                SurveyCreationInsightsApiActions.findSurveysFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
        );
      }),
    ),
  );

  /**
   * This is used to show the total number of respondents for the current search criteria.
   */
  public readonly respondentCounts$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.getRespondentCounts),
      debounceTime(100),
      switchMap(({ filterType, allSurveyGroupIds, statuses }) => {
        if (!allSurveyGroupIds.length) {
          return of(
            SurveyCreationInsightsApiActions.respondentCountsSuccess({
              totalCount: 0,
            }),
          );
        }

        return this._psidService
          .getCounts(filterType === 'exclude', allSurveyGroupIds, statuses)
          .pipe(
            map((totalCount) =>
              SurveyCreationInsightsApiActions.respondentCountsSuccess({
                totalCount,
              }),
            ),
            this.psCatchError((error) =>
              of(
                SurveyCreationInsightsApiActions.respondentCountsFailure({
                  error: error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  /**
   * Typeahead for the Billing Number / Survey Title criteria search widgets — a
   * lightweight, single-keyword survey lookup, distinct from `findSurveys$`'s
   * combined multi-criteria lookup. `switchMap` cancels a still-in-flight search
   * as soon as a newer keystroke arrives (legacy has no such cancellation).
   */
  public readonly criteriaSurveysSearch$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.searchCriteriaSurveys),
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap(
        ({ searchType, value, dateRange, countryCode, languageCode }) => {
          if (value.trim().length < MIN_SEARCH_LENGTH) {
            return of(
              SurveyCreationInsightsApiActions.searchCriteriaSurveysSuccess({
                surveys: [],
              }),
            );
          }

          return concat(
            of(SurveyCreationInsightsApiActions.searchCriteriaSurveysLoading()),
            this._surveyService
              .searchSurveySummaries(
                CriteriaSearchParams.forCriteria(
                  searchType,
                  value,
                  dateRange,
                  countryCode,
                  languageCode,
                ).build(),
              )
              .pipe(
                map((surveys) =>
                  SurveyCreationInsightsApiActions.searchCriteriaSurveysSuccess(
                    { surveys },
                  ),
                ),
                this.psCatchError((error) =>
                  of(
                    SurveyCreationInsightsApiActions.searchCriteriaSurveysFailure(
                      { error: error.message },
                    ),
                  ),
                ),
              ),
          );
        },
      ),
    ),
  );

  /**
   * Typeahead for the Groups picker's manual survey search — same `/surveys/lookup`
   * endpoint as the criteria search, fixed to `searchKey: 'id'`.
   */
  public readonly groupSurveysSearch$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationInsightsActions.searchGroupSurveys),
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap(({ value, dateRange, countryCode, languageCode }) => {
        if (value.trim().length < MIN_SEARCH_LENGTH) {
          return of(
            SurveyCreationInsightsApiActions.searchGroupSurveysSuccess({
              surveys: [],
            }),
          );
        }

        return concat(
          of(SurveyCreationInsightsApiActions.searchGroupSurveysLoading()),
          this._surveyService
            .searchSurveySummaries(
              CriteriaSearchParams.forGroup(
                value,
                dateRange,
                countryCode,
                languageCode,
              ).build(),
            )
            .pipe(
              map((surveys) =>
                SurveyCreationInsightsApiActions.searchGroupSurveysSuccess({
                  surveys,
                }),
              ),
              this.psCatchError((error) =>
                of(
                  SurveyCreationInsightsApiActions.searchGroupSurveysFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
        );
      }),
    ),
  );
}
